import { consentStorage, type ConsentDecision } from './storage'
import { configureGoogleTag, revokeGoogleTagConsent } from './googleTag'
import { configureMetaPixel, revokeMetaPixelConsent } from './metaPixel'

const completedContactDisclosureAttempts = new Set<string>()

function hasAdConsent() {
  return consentStorage.getConsent() === 'granted'
}

function getStringProp(props: Record<string, unknown> | undefined, key: string) {
  const value = props?.[key]
  return typeof value === 'string' ? value : undefined
}

function getContactAttemptKey(props: Record<string, unknown> | undefined) {
  return getStringProp(props, 'contact_attempt_id')
}

function trackAdPlatformLead(props: Record<string, unknown> | undefined) {
  if (!hasAdConsent()) return

  const attemptKey = getContactAttemptKey(props)
  if (attemptKey) {
    if (completedContactDisclosureAttempts.has(attemptKey)) return
    completedContactDisclosureAttempts.add(attemptKey)
  }

  const conversionLabel = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID
  if (googleAdsId && conversionLabel && typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: `${googleAdsId}/${conversionLabel}`,
    })
  }

  if (import.meta.env.VITE_META_PIXEL_ID && typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', {
      content_name: EVENTS.PROFESSIONAL_CONTACT_DISCLOSED,
      professional_id: getStringProp(props, 'professional_id'),
      professional_name: getStringProp(props, 'professional_name'),
      profession_name: getStringProp(props, 'profession_name'),
      contact_attempt_id: attemptKey,
    })
  }
}

function maybeMirrorEventToAdPlatforms(event: string, props?: Record<string, unknown>) {
  if (event !== EVENTS.PROFESSIONAL_CONTACT_DISCLOSED) return

  trackAdPlatformLead(props)
}

function sanitizePagePath(path: string) {
  return new URL(path || '/', window.location.origin).pathname
}

function buildPageLocation(path: string) {
  return `${window.location.origin}${path}`
}

function configureAdPlatforms() {
  configureGoogleTag()
  configureMetaPixel()
}

function revokeAdPlatformConsent() {
  revokeGoogleTagConsent()
  revokeMetaPixelConsent()
}

// ============================================================================
// Core wrapper
// ============================================================================

export const analytics = {
  capture(event: string, props?: Record<string, unknown>) {
    window.posthog?.capture(event, props)
    maybeMirrorEventToAdPlatforms(event, props)
  },
  identify(distinctId: string, props?: Record<string, unknown>) {
    window.posthog?.identify(distinctId, props)
  },
  reset() {
    window.posthog?.reset()
  },
  trackPageView(path: string) {
    if (!hasAdConsent()) return

    configureAdPlatforms()
    const pagePath = sanitizePagePath(path)

    if (import.meta.env.VITE_GA4_MEASUREMENT_ID && typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        send_to: import.meta.env.VITE_GA4_MEASUREMENT_ID,
        page_path: pagePath,
        page_location: buildPageLocation(pagePath),
        page_title: document.title,
      })
    }

    if (import.meta.env.VITE_META_PIXEL_ID && typeof window.fbq === 'function') {
      window.fbq('track', 'PageView')
    }
  },
  // Consent — PostHog is initialized with opt_out_capturing_by_default in index.html,
  // so nothing is captured until optIn() is called from the consent banner.
  optIn() {
    window.posthog?.opt_in_capturing?.()
    configureAdPlatforms()
  },
  optOut() {
    revokeAdPlatformConsent()
    window.posthog?.opt_out_capturing?.()
  },
  recordConsentDecision(decision: ConsentDecision) {
    // A refusal cannot be measured through PostHog without overriding that refusal.
    // Until a privacy-reviewed first-party aggregate endpoint exists, only accepted
    // decisions are observable.
    if (decision !== 'granted') return
    window.posthog?.capture(EVENTS.COOKIE_CONSENT_DECISION, { decision: 'accepted' })
  },
}

export function createContactAttemptId(professionalId: string) {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  return `${professionalId}:${Date.now()}:${Math.random().toString(36).slice(2)}`
}

// ============================================================================
// Event name constants — use these everywhere; string literals silently create
// phantom events in PostHog on typo.
// ============================================================================

export const EVENTS = {
  ACCOUNT_CREATED: 'account_created',
  COOKIE_CONSENT_DECISION: 'cookie_consent_decision',
  PROFESSIONAL_CONTACT_DISCLOSED: 'professional_contact_disclosed',
  WHATSAPP_CONTACT: 'whatsapp_contact',
} as const

export const WHATSAPP_STAGE = {
  UNAUTHENTICATED: 'unauthenticated',
  UNVALIDATED: 'unvalidated',
  ATTEMPTED: 'attempted',
  FAILED: 'failed',
} as const

export const WHATSAPP_VIA = {
  DIRECT: 'direct',
  PENDING_ACTION: 'pending_action',
} as const

// ============================================================================
// Prop builder — shared shape for whatsapp_contact across both call sites
// ============================================================================

export function buildWhatsappContactProps(
  base: {
    professional_id: string
    professional_name?: string
    profession_name?: string
    contact_attempt_id?: string
  },
  via: (typeof WHATSAPP_VIA)[keyof typeof WHATSAPP_VIA]
) {
  return { ...base, via }
}

// ============================================================================
// Event catalogue
// ============================================================================
//
// whatsapp_contact
//   Fires on every WhatsApp contact interaction. A single logical contact attempt by an
//   unauthenticated/unvalidated user emits TWO events: a redirect-intent stage at click
//   time, then 'attempted'/'failed' after login resumes the pending action. Always filter by
//   `stage` — never use raw count.
//
//   stage:
//     'unauthenticated' — clicked but not logged in; redirect to login
//     'unvalidated'     — logged in but account not validated; redirect to profile
//     'attempted'       — the contact API disclosed a number and the app is about to call
//                         window.open. This is a navigation-attempt signal, not proof that
//                         the browser opened WhatsApp or that the user sent a message.
//     'failed'          — API error, no contact number on file, or corrupted pending
//                         action with no professional_id (reason prop set)
//
//   via:
//     'direct'          — triggered via the WhatsApp button directly (any auth state)
//     'pending_action'  — resumed from a stored pending action after login/validation redirect
//
//   Canonical funnel (single source of truth — every consumer must filter by stage):
//     attempts        = count(stage in ['attempted','failed'])        // actual contacts tried
//     redirect-intent = count(stage in ['unauthenticated','unvalidated'])  // gated, will resume post-auth
//     disclosure-rate = count(stage = 'attempted') / attempts
//   Do not sum all stages as "total clicks": a gated click is later counted again as
//   attempted/failed once the pending action resumes, so it would double-count.
//
// professional_contact_disclosed
//   Fires after the validated contact API returns a non-empty number and before the app
//   attempts browser navigation. This is the paid-media conversion source. Google Ads and
//   Meta Lead forwarding is deduplicated by contact_attempt_id so a resumed action cannot
//   convert twice within the same app session. It does not claim that WhatsApp opened or
//   that a message was sent.
//
// account_created
//   SEMANTIC (decided): raw backend account-creation count, NOT onboarding success.
//   Fires right after registerMutation resolves — before the profile refetch and before
//   identify(). It therefore counts every account the backend created, including ones
//   where the subsequent refetch blips and the session never establishes. This is
//   intentional: we want creation volume, not session-established volume.
//
//   Anonymous-person edge: identify() runs only after a successful refetch. With
//   person_profiles:'identified_only', if refetch fails the event lands on an anonymous
//   person and won't merge into a user profile. Accepted under the raw-creation semantic.
//
//   identifier_type: 'phone' | 'email'
//
// cookie_consent_decision
//   Fires only after the visitor accepts analytics. Declined and dismissed decisions
//   are deliberately absent because recording them through PostHog would require
//   overriding the visitor's refusal. There is no approved first-party aggregate
//   endpoint for an anonymous refusal denominator.
